import React from 'react';
import { VaultKeTheme } from '../../utils/theme';

const Docs: React.FC = () => {
  const styles: { [key: string]: React.CSSProperties } = {
    page: {
      minHeight: '100vh',
      backgroundColor: VaultKeTheme.colors.background,
      color: VaultKeTheme.colors.text,
    },
    main: {
      paddingTop: '80px',
      paddingBottom: VaultKeTheme.spacing.xxl,
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: `0 ${VaultKeTheme.spacing.xl}`,
    },
    header: {
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    title: {
      ...VaultKeTheme.typography.h1,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.lg,
    },
    subtitle: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.textSecondary,
      maxWidth: '600px',
      margin: '0 auto',
    },
    search: {
      maxWidth: '600px',
      margin: '0 auto',
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    searchInput: {
      ...VaultKeTheme.components.input,
      width: '100%',
      padding: '16px',
      fontSize: '1.1rem',
    },
    sections: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: VaultKeTheme.spacing.xl,
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    section: {
      ...VaultKeTheme.components.card,
    },
    sectionTitle: {
      ...VaultKeTheme.typography.h4,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.lg,
    },
    docList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    docItem: {
      marginBottom: VaultKeTheme.spacing.md,
    },
    docLink: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
      textDecoration: 'none',
      cursor: 'pointer',
      transition: 'color 0.2s ease',
    },
    apiSection: {
      ...VaultKeTheme.components.card,
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    codeBlock: {
      backgroundColor: VaultKeTheme.colors.surfaceVariant,
      border: `1px solid ${VaultKeTheme.colors.border}`,
      borderRadius: VaultKeTheme.borderRadius.md,
      padding: VaultKeTheme.spacing.lg,
      margin: VaultKeTheme.spacing.lg,
      fontFamily: 'monospace',
      fontSize: '0.9rem',
      color: VaultKeTheme.colors.text,
      overflow: 'auto',
    },
    endpoint: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.primary,
      fontFamily: 'monospace',
      fontWeight: '600',
      marginBottom: VaultKeTheme.spacing.sm,
    },
    method: {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: VaultKeTheme.borderRadius.sm,
      fontSize: '0.8rem',
      fontWeight: 'bold',
      marginRight: VaultKeTheme.spacing.sm,
    },
    getMethod: {
      backgroundColor: VaultKeTheme.colors.success,
      color: VaultKeTheme.colors.background,
    },
    postMethod: {
      backgroundColor: VaultKeTheme.colors.info,
      color: VaultKeTheme.colors.background,
    },
  };

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <div style={styles.container}>
          <div style={styles.header}>
            <h1 style={styles.title}>Documentation</h1>
            <p style={styles.subtitle}>
              Everything you need to know about using VaultKe effectively.
              From getting started to advanced features and API integration.
            </p>
          </div>

          <div style={styles.search}>
            <input
              style={styles.searchInput}
              type="text"
              placeholder="Search documentation..."
            />
          </div>

          <div style={styles.sections}>
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Getting Started</h3>
              <ul style={styles.docList}>
                <li style={styles.docItem}>
                  <a style={styles.docLink} href="#quick-start">Quick Start Guide</a>
                </li>
                <li style={styles.docItem}>
                  <a style={styles.docLink} href="#account-setup">Account Setup</a>
                </li>
                <li style={styles.docItem}>
                  <a style={styles.docLink} href="#creating-chama">Creating Your First Chama</a>
                </li>
                <li style={styles.docItem}>
                  <a style={styles.docLink} href="#inviting-members">Inviting Members</a>
                </li>
              </ul>
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Features</h3>
              <ul style={styles.docList}>
                <li style={styles.docItem}>
                  <a style={styles.docLink} href="#contributions">Managing Contributions</a>
                </li>
                <li style={styles.docItem}>
                  <a style={styles.docLink} href="#loans">Loan Management</a>
                </li>
                <li style={styles.docItem}>
                  <a style={styles.docLink} href="#meetings">Meeting Management</a>
                </li>
                <li style={styles.docItem}>
                  <a style={styles.docLink} href="#reports">Financial Reports</a>
                </li>
              </ul>
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Mobile App</h3>
              <ul style={styles.docList}>
                <li style={styles.docItem}>
                  <a style={styles.docLink} href="#app-installation">App Installation</a>
                </li>
                <li style={styles.docItem}>
                  <a style={styles.docLink} href="#app-features">App Features</a>
                </li>
                <li style={styles.docItem}>
                  <a style={styles.docLink} href="#offline-mode">Offline Mode</a>
                </li>
                <li style={styles.docItem}>
                  <a style={styles.docLink} href="#notifications">Push Notifications</a>
                </li>
              </ul>
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Security</h3>
              <ul style={styles.docList}>
                <li style={styles.docItem}>
                  <a style={styles.docLink} href="#data-protection">Data Protection</a>
                </li>
                <li style={styles.docItem}>
                  <a style={styles.docLink} href="#two-factor">Two-Factor Authentication</a>
                </li>
                <li style={styles.docItem}>
                  <a style={styles.docLink} href="#encryption">End-to-End Encryption</a>
                </li>
                <li style={styles.docItem}>
                  <a style={styles.docLink} href="#compliance">Compliance & Regulations</a>
                </li>
              </ul>
            </div>
          </div>

          <div style={styles.apiSection}>
            <h3 style={styles.sectionTitle}>API Reference</h3>
            <p style={{ ...VaultKeTheme.typography.body2, color: VaultKeTheme.colors.textSecondary, marginBottom: VaultKeTheme.spacing.lg }}>
              Integrate VaultKe with your existing systems using our REST API.
            </p>

            <div style={styles.codeBlock}>
              <div style={{ ...styles.endpoint, marginBottom: VaultKeTheme.spacing.md }}>
                <span style={{ ...styles.method, ...styles.getMethod }}>GET</span>
                /api/v1/chamas
              </div>
              <p style={{ ...VaultKeTheme.typography.caption, color: VaultKeTheme.colors.textSecondary, marginBottom: VaultKeTheme.spacing.sm }}>
                Retrieve a list of chamas for the authenticated user.
              </p>

              <div style={{ ...styles.endpoint, marginTop: VaultKeTheme.spacing.lg }}>
                <span style={{ ...styles.method, ...styles.postMethod }}>POST</span>
                /api/v1/chamas
              </div>
              <p style={{ ...VaultKeTheme.typography.caption, color: VaultKeTheme.colors.textSecondary }}>
                Create a new chama with the provided details.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Docs;